import React, { useState, useEffect, useRef } from 'react';
import logo from '../../../assets/image/logoadmin.png';
import { authService } from '../../../services/authService';
import { ROUTES } from '../../../utils/constants';
import { useNavigate } from 'react-router-dom';
import './header.css'; // We'll create this CSS file
import {
  Bell,
  Search,
  LogOut,
  User,
  Settings,
  HelpCircle,
  ChevronDown,
  Menu,
  X,
  Sun,
  Moon,
  Mail
} from 'lucide-react';

interface HeaderProps {
  isMenuOpen: boolean;
  onMenuToggle: () => void;
  onThemeToggle?: () => void;
  isDarkMode?: boolean;
}

interface UserData {
  first_name?: string;
  last_name?: string;
  email?: string;
  role?: string;
  avatar?: string;
}

interface Notification {
  id: number;
  title: string;
  message: string;
  time: string;
  read: boolean;
  type: 'info' | 'success' | 'warning' | 'error';
}

const Header: React.FC<HeaderProps> = ({ 
  isMenuOpen, 
  onMenuToggle,
  onThemeToggle,
  isDarkMode = false 
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState<boolean>(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);

  const userData: UserData = JSON.parse(
    localStorage.getItem('user') || '{}'
  );

  // Mock notifications - replace with real API calls
  useEffect(() => {
    const mockNotifications: Notification[] = [
      {
        id: 1,
        title: 'New User Registration',
        message: 'John Doe has registered as a new user',
        time: '10 min ago',
        read: false,
        type: 'info'
      },
      {
        id: 2,
        title: 'Project Completed',
        message: 'FDC Project #1234 has been successfully completed',
        time: '1 hour ago',
        read: false,
        type: 'success'
      },
      {
        id: 3,
        title: 'System Update',
        message: 'Scheduled maintenance tonight at 2 AM',
        time: '3 hours ago',
        read: true,
        type: 'warning'
      },
      {
        id: 4,
        title: 'New Message',
        message: 'You have a new message from support team',
        time: '1 day ago',
        read: true,
        type: 'info'
      }
    ];
    
    setNotifications(mockNotifications);
    setUnreadCount(mockNotifications.filter(n => !n.read).length);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setIsNotificationsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async (): Promise<void> => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout API failed:', error);
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('role_id');
      localStorage.removeItem('email');
      localStorage.removeItem('permissions');
      localStorage.removeItem('user');

      navigate(ROUTES.SIGNIN, { replace: true });
    }
  };

  const handleMarkAllAsRead = () => {
    const updatedNotifications = notifications.map(notif => ({ ...notif, read: true }));
    setNotifications(updatedNotifications);
    setUnreadCount(0);
  };

  const handleNotificationClick = (id: number) => {
    const updatedNotifications = notifications.map(notif => 
      notif.id === id ? { ...notif, read: true } : notif
    );
    setNotifications(updatedNotifications);
    setUnreadCount(updatedNotifications.filter(n => !n.read).length);
    setIsNotificationsOpen(false);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      console.log('Searching for:', searchQuery);
      // Implement search functionality here
      setShowSearch(false);
    }
  };

  return (
    <header className={`admin-header ${isDarkMode ? 'dark-mode' : ''}`}>
      {/* Left Section */}
      <div className="header-left">
        <button 
          className="menu-toggle"
          onClick={onMenuToggle}
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
        >
          {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>

        <div className="logo">
          <img src={logo} alt="Admin Logo" className="logo-img" />
          <div className="logo-text">
            <span className="logo-title">Admin Panel</span>
            <span className="logo-subtitle">Management System</span>
          </div>
        </div>
      </div>

      

      {/* Right Section - Actions */}
      <div className="header-right">
        {/* Search Toggle Button */}
        {/* <button 
          className="header-action search-toggle"
          onClick={() => setShowSearch(!showSearch)}
          aria-label="Search"
        >
          <Search size={20} />
        </button> */}

        {/* Theme Toggle */}
        {/* {onThemeToggle && (
          <button 
            className="header-action theme-toggle"
            onClick={onThemeToggle}
            aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
          >
            {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
          </button>
        )} */}

        {/* Notifications */}
        {/* <div className="notifications-container" ref={notificationsRef}>
          <button 
            className="header-action notifications-toggle"
            onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
            aria-label="Notifications"
          >
            <Bell size={20} />
            {unreadCount > 0 && (
              <span className="notification-badge">{unreadCount}</span>
            )}
          </button>

          {isNotificationsOpen && (
            <div className="notifications-dropdown">
              <div className="notifications-header">
                <h3>Notifications</h3>
                {unreadCount > 0 && (
                  <button 
                    className="mark-all-read"
                    onClick={handleMarkAllAsRead}
                  >
                    Mark all as read
                  </button>
                )}
              </div>
              
              <div className="notifications-list">
                {notifications.length > 0 ? (
                  notifications.map(notification => (
                    <div 
                      key={notification.id}
                      className={`notification-item ${notification.read ? 'read' : 'unread'} ${notification.type}`}
                      onClick={() => handleNotificationClick(notification.id)}
                    >
                      <div className="notification-icon">
                        {notification.type === 'success' && '✓'}
                        {notification.type === 'warning' && '⚠'}
                        {notification.type === 'error' && '✗'}
                        {notification.type === 'info' && 'ℹ'}
                      </div>
                      <div className="notification-content">
                        <h4>{notification.title}</h4>
                        <p>{notification.message}</p>
                        <span className="notification-time">{notification.time}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-notifications">
                    <Bell size={40} />
                    <p>No new notifications</p>
                  </div>
                )}
              </div>

              <div className="notifications-footer">
                <button 
                  className="view-all-notifications"
                  onClick={() => {
                    navigate('/admin/notifications');
                    setIsNotificationsOpen(false);
                  }}
                >
                  View all notifications
                </button>
              </div>
            </div>
          )}
        </div> */}

        {/* User Profile */}
        <div className="user-profile-container" ref={dropdownRef}>
          <button 
            className="user-profile-toggle"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            <div className="user-avatar">
              {userData.avatar ? (
                <img src={userData.avatar} alt={userData.first_name} />
              ) : (
                <div className="avatar-fallback">
                  {userData.first_name?.[0]?.toUpperCase() || 'U'}
                </div>
              )}
            </div>
            
            <div className="user-info">
              <span className="user-name">
                {userData.first_name ? `${userData.first_name} ${userData.last_name || ''}`.trim() : 'User'}
              </span>
            </div>
            
            <ChevronDown size={16} className={`dropdown-arrow ${isDropdownOpen ? 'open' : ''}`} />
          </button>

          {isDropdownOpen && (
            <div className="user-dropdown">
              <div className="dropdown-header">
                <div className="dropdown-user-info">
                  <div className="dropdown-avatar">
                    {userData.avatar ? (
                      <img src={userData.avatar} alt={userData.first_name} />
                    ) : (
                      <div className="avatar-fallback-large">
                        {userData.first_name?.[0]?.toUpperCase() || 'U'}
                      </div>
                    )}
                  </div>
                  <div>
                    <h4>{userData.first_name ? `${userData.first_name} ${userData.last_name || ''}`.trim() : 'User'}</h4>
                    <p>{userData.email || 'user@example.com'}</p>
                  </div>
                </div>
              </div>

              <div className="dropdown-items">
                <button 
                  className="dropdown-item"
                  onClick={() => {
                    navigate('/admin/profile');
                    setIsDropdownOpen(false);
                  }}
                >
                  <User size={18} />
                  <span>My Profile</span>
                </button>

                <button 
                  className="dropdown-item"
                  onClick={() => {
                    navigate('/admin/settings');
                    setIsDropdownOpen(false);
                  }}
                >
                  <Settings size={18} />
                  <span>Settings</span>
                </button>

                <button 
                  className="dropdown-item"
                  onClick={() => {
                    navigate('/admin/help');
                    setIsDropdownOpen(false);
                  }}
                >
                  <HelpCircle size={18} />
                  <span>Help & Support</span>
                </button>

                <div className="dropdown-divider" />

                <button 
                  className="dropdown-item logout"
                  onClick={handleLogout}
                >
                  <LogOut size={18} />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;